import React from 'react';

/* The JM-Trade mark: an iceberg defined by its waterline.

   About one ninth of an iceberg sits above water, and that is the product — the operator
   sees a conclusion, the system underneath carries the backtests, the risk engine and the
   audit trail. It is also the honest thing to say about risk. The **waterline is the mark**,
   not the ice: delete the rule and what is left is a shape, which is the test the brief set
   and the reason two earlier attempts were thrown away.

   Variant D, "Split": the exposed and submerged forms share the waterline edge exactly, so
   it reads as one body the rule happens to cross rather than as two stacked shapes.

   ## Rules

   - **`currentColor` throughout.** The mark inherits from whatever it sits in, so it is
     `--accent` in the sidebar, `--text-primary` in a one-colour context, and correct in
     both themes without a second asset. Never give it a fill of its own.
   - **The submerged mass is the same colour at 0.42 opacity**, never a second hue. Two
     tones maximum, and the dimmer one is always the part below the water.
   - **Never a semantic colour.** Not profit-green, loss-red, live-amber, paper-blue or
     attention-yellow. Those five mean money states; a mark wearing one is a misread.
   - **Below 24px use `variant="favicon"`.** The facets and the 1.5-unit rule are sub-pixel
     at 16px; the favicon form is rebuilt, not scaled, and shipping the full mark small is
     how a logo becomes a grey smudge.
   - Clear space is **one waterline length** (the rule spans the full 32 units) on every
     side. Minimum size 16px for the favicon form, 24px for the mark.

   The wordmark is Geist 600 at -0.02em, converted to outlines, so it renders identically
   with or without the webfont. Do not replace it with live text.
*/

const WORDMARK_D = 'M 8.56 0.45 Q 6.38 0.45 4.82 -0.50 Q 3.25 -1.45 2.41 -3.12 Q 1.58 -4.78 1.55 -6.93 L 5.25 -7.12 Q 5.28 -4.92 6.15 -3.83 Q 7.01 -2.74 8.56 -2.74 Q 10.15 -2.74 11.02 -3.84 Q 11.89 -4.95 11.89 -7.12 L 11.89 -20.00 L 15.55 -20.00 L 15.55 -7.12 Q 15.55 -4.81 14.68 -3.10 Q 13.80 -1.40 12.24 -0.47 Q 10.68 0.45 8.56 0.45 Z M 19.07 -0.00 L 19.07 -20.00 L 24.01 -20.00 L 29.53 -4.51 L 35.04 -20.00 L 39.98 -20.00 L 39.98 -0.00 L 36.32 -0.00 L 36.32 -13.80 L 31.27 -0.03 L 27.76 -0.03 L 22.73 -13.80 L 22.73 -0.00 Z M 42.77 -6.69 L 42.77 -9.74 L 52.33 -9.74 L 52.33 -6.69 Z M 59.26 -0.00 L 59.26 -16.81 L 53.19 -16.81 L 53.19 -20.00 L 69.00 -20.00 L 69.00 -16.81 L 62.93 -16.81 L 62.93 -0.00 Z M 69.48 -0.00 L 69.48 -15.04 L 72.85 -15.04 L 72.98 -10.86 L 72.69 -10.94 Q 73.03 -13.10 73.96 -14.07 Q 74.90 -15.04 76.52 -15.04 L 77.92 -15.04 L 77.92 -11.95 L 76.52 -11.95 Q 75.36 -11.95 74.60 -11.61 Q 73.84 -11.27 73.46 -10.55 Q 73.08 -9.84 73.08 -8.66 L 73.08 -0.00 Z M 84.78 0.34 Q 82.43 0.34 80.99 -0.74 Q 79.55 -1.83 79.55 -3.76 Q 79.55 -5.72 80.77 -6.82 Q 81.98 -7.91 84.44 -8.39 L 89.44 -9.37 Q 89.44 -10.97 88.71 -11.78 Q 87.97 -12.59 86.55 -12.59 Q 85.23 -12.59 84.48 -11.99 Q 83.74 -11.39 83.48 -10.25 L 79.81 -10.43 Q 80.24 -12.81 81.99 -14.09 Q 83.74 -15.38 86.55 -15.38 Q 89.76 -15.38 91.40 -13.77 Q 93.05 -12.15 93.05 -9.14 L 93.05 -3.66 Q 93.05 -3.06 93.25 -2.84 Q 93.46 -2.63 93.88 -2.63 L 94.36 -2.63 L 94.36 -0.00 Q 94.19 0.05 93.81 0.09 Q 93.44 0.12 93.05 0.12 Q 92.12 0.12 91.39 -0.18 Q 90.66 -0.49 90.25 -1.23 Q 89.85 -1.96 89.85 -3.26 L 90.17 -3.04 Q 89.93 -2.05 89.19 -1.28 Q 88.46 -0.51 87.32 -0.09 Q 86.19 0.34 84.78 0.34 Z M 85.52 -2.29 Q 86.72 -2.29 87.60 -2.76 Q 88.47 -3.23 88.96 -4.08 Q 89.44 -4.94 89.44 -6.07 L 89.44 -6.92 L 85.54 -6.13 Q 84.35 -5.89 83.82 -5.39 Q 83.28 -4.90 83.28 -4.10 Q 83.28 -3.24 83.87 -2.76 Q 84.45 -2.29 85.52 -2.29 Z M 101.56 0.34 Q 99.64 0.34 98.24 -0.63 Q 96.84 -1.59 96.08 -3.35 Q 95.33 -5.12 95.33 -7.52 Q 95.33 -9.93 96.09 -11.69 Q 96.85 -13.45 98.26 -14.42 Q 99.66 -15.38 101.56 -15.38 Q 103.13 -15.38 104.33 -14.73 Q 105.52 -14.08 106.13 -12.91 L 106.13 -20.00 L 109.74 -20.00 L 109.74 -0.00 L 106.32 -0.00 L 106.24 -2.23 Q 105.62 -1.02 104.38 -0.34 Q 103.14 0.34 101.56 0.34 Z M 102.65 -2.58 Q 103.77 -2.58 104.54 -3.15 Q 105.32 -3.73 105.72 -4.84 Q 106.13 -5.94 106.13 -7.52 Q 106.13 -9.13 105.72 -10.23 Q 105.32 -11.33 104.54 -11.90 Q 103.77 -12.46 102.65 -12.46 Q 101.00 -12.46 100.03 -11.14 Q 99.06 -9.82 99.06 -7.52 Q 99.06 -5.26 100.04 -3.92 Q 101.01 -2.58 102.65 -2.58 Z M 119.62 0.34 Q 117.37 0.34 115.72 -0.63 Q 114.07 -1.60 113.16 -3.37 Q 112.26 -5.14 112.26 -7.52 Q 112.26 -9.90 113.16 -11.66 Q 114.07 -13.41 115.70 -14.40 Q 117.34 -15.38 119.55 -15.38 Q 121.70 -15.38 123.31 -14.42 Q 124.92 -13.46 125.81 -11.66 Q 126.69 -9.85 126.69 -7.32 L 126.69 -6.52 L 116.00 -6.52 Q 116.11 -4.53 117.08 -3.54 Q 118.05 -2.55 119.67 -2.55 Q 120.87 -2.55 121.67 -3.08 Q 122.47 -3.62 122.79 -4.61 L 126.47 -4.38 Q 125.87 -2.20 124.05 -0.93 Q 122.23 0.34 119.62 0.34 Z M 116.00 -8.94 L 122.95 -8.94 Q 122.83 -10.77 121.91 -11.66 Q 120.99 -12.54 119.55 -12.54 Q 118.11 -12.54 117.16 -11.61 Q 116.22 -10.69 116.00 -8.94 Z';
const WORDMARK_W = 127.80;
const WORDMARK_H = 20.00;

/* Gap between mark and wordmark in the horizontal lockup, and between them when stacked.
   Both are tuned to the mark's 32-unit box rather than to the cap height, because the mark
   is the fixed element and the type is what moves. */
const GAP_H = 11;
const GAP_V = 11;

/* Stacked only — see the note in that branch. 2.25 puts the mark at 72 units against the
   wordmark's 128, a little over half, which is where the two read as one object. */
const STACK_SCALE = 2.25;

function Mark({ favicon }) {
  return favicon ? (
    <>
      <path d="M9 12 L23 12 L27 17 L24 24 L15 28 L6 21 L5.5 15 Z" fill="currentColor" opacity="0.42"/>
      <path d="M1.5 12 H30.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"/>
      <path d="M11 12 L16 4 L21 12 Z" fill="currentColor"/>
    </>
  ) : (
    <>
      <path d="M11 11.5 L21 11.5 L24.5 17 L20.5 24 L12.5 26.5 L7.5 19 Z" fill="currentColor" opacity="0.42"/>
      <path d="M2 11.5 H30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
      <path d="M11 11.5 L14 4 L17 7.5 L19 5.5 L21 11.5 Z" fill="currentColor"/>
    </>
  );
}

export function Wordmark({ variant = 'horizontal', title = 'JM-Trade', style, ...rest }) {
  const common = {
    fill: 'none', role: 'img', 'aria-label': title,
    style: { display: 'block', height: 'auto', ...style },
    ...rest,
  };

  if (variant === 'mark' || variant === 'favicon') {
    return (
      <svg viewBox="0 0 32 32" {...common}>
        <Mark favicon={variant === 'favicon'} />
      </svg>
    );
  }

  if (variant === 'wordmark') {
    return (
      <svg viewBox={`0 0 ${WORDMARK_W} ${WORDMARK_H}`} {...common}>
        <g transform={`translate(0 ${WORDMARK_H})`}>
          <path d={WORDMARK_D} fill="currentColor" />
        </g>
      </svg>
    );
  }

  if (variant === 'stacked') {
    /* The mark is scaled up here and only here. In the horizontal lockup a 32-unit mark
       beside a 128-unit wordmark is the conventional 1:4 and looks right, because the eye
       reads them as one line. Stacked, the same ratio puts a quarter-width mark on top of a
       full-width wordmark, and it reads as an afterthought perched on the type rather than
       as the senior element. STACK_SCALE takes it to a little over half the wordmark's
       width, which is where the two start to look like one object. */
    const m = 32 * STACK_SCALE;
    const w = Math.max(m, WORDMARK_W);
    const h = m + GAP_V + WORDMARK_H;
    return (
      <svg viewBox={`0 0 ${w} ${h}`} {...common}>
        <g transform={`translate(${(w - m) / 2} 0) scale(${STACK_SCALE})`}><Mark /></g>
        <g transform={`translate(${(w - WORDMARK_W) / 2} ${h})`}>
          <path d={WORDMARK_D} fill="currentColor" />
        </g>
      </svg>
    );
  }

  // horizontal — the default lockup. The baseline sits on the mark's optical centre, which
  // is below its geometric centre because the ink is bottom-heavy.
  const x = 32 + GAP_H;
  return (
    <svg viewBox={`0 0 ${x + WORDMARK_W} 32`} {...common}>
      <Mark />
      <g transform={`translate(${x} ${16 + WORDMARK_H / 2})`}>
        <path d={WORDMARK_D} fill="currentColor" />
      </g>
    </svg>
  );
}
